import React, { memo, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import _classNames from 'classnames';
import _isEmpty from 'lodash/isEmpty';
import IconButton from '@material-ui/core/IconButton';
import InfoIcon from '@material-ui/icons/Info';
import CloseIcon from '@material-ui/icons/Close';
import Popover from '@material-ui/core/Popover';
import _get from 'lodash/get';
import useIntervalInformation from './hooks/useIntervalInformation';

const useStyles = makeStyles(() => ({
  selectRoot: {
    marginTop: 4,
    '& input': {
      height: '1.2em',
      padding: '16px 14px',
    },
    '& select': {
      height: '1.2em',
      lineHeight: '1.35',
      padding: '16px 14px',
    },
  },
  disabledColor: {
    color: '#999999',
  },
  scoreComponentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  fontSize: {
    fontSize: '1.063rem',
  },
  infoIcon: {
    padding: 0,
    marginTop: -2,
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    borderRadius: '8px !important',
    marginTop: 10,
    padding: 20,
    paddingTop: 5,
    paddingRight: 5,
    boxShadow: '0px 4px 10px 0px rgba(0, 0, 0, 0.10)',
    color: '#000',
  },
  paperContent: {
    paddingRight: 15,
  },
}));

const ScoreComponent = ({
  component,
  index,
  onScoreComponentChange,
  scoreComponents,
  regularScoring,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const classes = useStyles();

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { renderIntervalInformation } = useIntervalInformation({
    intervals: component.intervals,
  });

  const open = Boolean(anchorEl);
  const id = open ? 'interval-popover' : undefined;

  const diff = component.max - component.min + 1;
  const intervalsLength = _get(component, 'intervals.length', 0);

  return (
    <Grid item xs={3} className={classes.scoreComponentWrapper}>
      <Box display="flex" alignItems="center" mb={1}>
        <Typography variant="subtitle1" color="textPrimary">
          <Box component="span" mr={1}>
            {component.name}
          </Box>
          {intervalsLength ? (
            <IconButton
              className={classes.infoIcon}
              aria-describedby={id}
              onClick={handleClick}
              aria-label="Info"
            >
              <InfoIcon />
            </IconButton>
          ) : null}
        </Typography>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          classes={{
            paper: classes.paper,
          }}
        >
          <Box minWidth={intervalsLength * 100} maxWidth={500}>
            <Box display="flex" justifyContent="flex-end">
              <IconButton onClick={handleClose} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box className={classes.paperContent}>
              {renderIntervalInformation()}
            </Box>
          </Box>
        </Popover>
      </Box>
      <TextField
        label={`Score component ${index + 1}`}
        onChange={onScoreComponentChange(index)}
        classes={{
          root: classes.selectRoot,
        }}
        value={regularScoring[index]}
        fullWidth
        select
        SelectProps={{
          native: true,
          classes: {
            root: _classNames({
              [classes.disabledColor]: _isEmpty(scoreComponents.overallScore),
            }),
          },
        }}
        InputProps={{
          classes: {
            root: classes.fontSize,
          },
        }}
        InputLabelProps={{
          classes: {
            root: classes.fontSize,
          },
        }}
        variant="outlined"
      >
        <option value="-1">Not applicable</option>
        {diff > 0 &&
          Array.from(new Array(diff)).map((_, value) => (
            <option
              value={value + scoreComponents.scoreComponents[index].min}
              key={value}
            >
              {value + scoreComponents.scoreComponents[index].min}
            </option>
          ))}
      </TextField>
    </Grid>
  );
};

export default memo(ScoreComponent);
